import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, Loader2, X } from 'lucide-react';
import FileUploader from './FileUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptyPhoto = () => ({ url: '', caption: '', tag: '' });
const emptyVideo = () => ({ title: '', tag: '', video_url: '', poster_url: '' });

const Input = ({ testId, value, onChange, placeholder }) => (
  <input
    data-testid={testId}
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-800/20"
    style={{ borderColor: '#E5E7EB' }}
  />
);

export default function GalleryEditor() {
  const [tab, setTab] = useState('photos');
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPhoto, setNewPhoto] = useState(null);
  const [newVideo, setNewVideo] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [p, v] = await Promise.all([
      axios.get(`${BACKEND_URL}/api/gallery/photos`),
      axios.get(`${BACKEND_URL}/api/gallery/videos`),
    ]);
    setPhotos(p.data); setVideos(v.data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const addPhoto = async () => {
    if (!newPhoto?.url) { alert('Please upload an image first'); return; }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/gallery/photos`, newPhoto, { withCredentials: true });
      setNewPhoto(null);
      await fetchAll();
    } finally { setSaving(false); }
  };
  const addVideo = async () => {
    if (!newVideo?.video_url) { alert('Please upload a video first'); return; }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/gallery/videos`, newVideo, { withCredentials: true });
      setNewVideo(null);
      await fetchAll();
    } finally { setSaving(false); }
  };
  const delPhoto = async (id) => {
    if (!window.confirm('Delete this photo?')) return;
    await axios.delete(`${BACKEND_URL}/api/admin/gallery/photos/${id}`, { withCredentials: true });
    fetchAll();
  };
  const delVideo = async (id) => {
    if (!window.confirm('Delete this video?')) return;
    await axios.delete(`${BACKEND_URL}/api/admin/gallery/videos/${id}`, { withCredentials: true });
    fetchAll();
  };

  if (loading) return <div className="py-12 text-center text-sm" style={{ color: '#6B7280' }}><Loader2 className="animate-spin inline mr-2" size={16} /> Loading gallery...</div>;

  return (
    <div data-testid="gallery-editor" className="max-w-5xl">
      <div className="flex gap-2 mb-4 border-b" style={{ borderColor: '#E5E7EB' }}>
        {['photos', 'videos'].map((tk) => (
          <button
            key={tk}
            data-testid={`gallery-editor-tab-${tk}`}
            onClick={() => setTab(tk)}
            className="px-4 py-2 text-sm font-semibold border-b-2 -mb-px capitalize"
            style={{ borderColor: tab === tk ? '#D97706' : 'transparent', color: tab === tk ? '#0A3F22' : '#9CA3AF' }}
          >
            {tk} ({tk === 'photos' ? photos.length : videos.length})
          </button>
        ))}
      </div>

      {tab === 'photos' && (
        <>
          {/* Add new photo card */}
          <div className="bg-white rounded-xl p-4 border mb-6" style={{ borderColor: '#E5E7EB' }}>
            {newPhoto ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-heading font-bold text-sm" style={{ color: '#0A3F22' }}>New Photo</h4>
                  <button onClick={() => setNewPhoto(null)} className="p-1.5 rounded" style={{ backgroundColor: '#F3F4F6', color: '#4A554A' }}><X size={14} /></button>
                </div>
                <FileUploader
                  testId="new-photo-upload"
                  currentUrl={newPhoto.url}
                  onUploaded={(url) => setNewPhoto((p) => ({ ...p, url }))}
                  accept="image/*"
                />
                <Input testId="new-photo-caption" value={newPhoto.caption} onChange={(v) => setNewPhoto((p) => ({ ...p, caption: v }))} placeholder="Caption (e.g. ARV Rotavator — Field View)" />
                <Input testId="new-photo-tag" value={newPhoto.tag} onChange={(v) => setNewPhoto((p) => ({ ...p, tag: v }))} placeholder="Tag (e.g. Rotavator, Customer)" />
                <button
                  data-testid="save-new-photo"
                  onClick={addPhoto}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white hover:opacity-90 disabled:opacity-60"
                  style={{ backgroundColor: '#0A3F22' }}
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  Add Photo
                </button>
              </div>
            ) : (
              <button
                data-testid="new-photo-btn"
                onClick={() => setNewPhoto(emptyPhoto())}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white hover:opacity-90"
                style={{ backgroundColor: '#D97706' }}
              >
                <Plus size={14} /> Add Photo
              </button>
            )}
          </div>

          {/* Photo grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {photos.map((p) => (
              <div key={p.id} data-testid={`photo-card-${p.id}`} className="bg-white rounded-xl overflow-hidden border relative group" style={{ borderColor: '#E5E7EB' }}>
                <div className="aspect-square bg-gray-100">
                  <img src={p.url} alt={p.caption} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
                <div className="p-2">
                  {p.tag && <span className="text-xs font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: '#EAF2EC', color: '#0A3F22' }}>{p.tag}</span>}
                  <p className="text-xs mt-1 truncate" style={{ color: '#4A554A' }}>{p.caption}</p>
                </div>
                <button
                  data-testid={`delete-photo-${p.id}`}
                  onClick={() => delPhoto(p.id)}
                  className="absolute top-2 right-2 p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ backgroundColor: '#DC2626', color: '#fff' }}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === 'videos' && (
        <>
          <div className="bg-white rounded-xl p-4 border mb-6" style={{ borderColor: '#E5E7EB' }}>
            {newVideo ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-heading font-bold text-sm" style={{ color: '#0A3F22' }}>New Video</h4>
                  <button onClick={() => setNewVideo(null)} className="p-1.5 rounded" style={{ backgroundColor: '#F3F4F6', color: '#4A554A' }}><X size={14} /></button>
                </div>
                <FileUploader
                  testId="new-video-upload"
                  currentUrl={newVideo.video_url}
                  onUploaded={(url) => setNewVideo((v) => ({ ...v, video_url: url }))}
                  accept="video/*"
                  label="Upload video"
                />
                <FileUploader
                  testId="new-video-poster-upload"
                  currentUrl={newVideo.poster_url}
                  onUploaded={(url) => setNewVideo((v) => ({ ...v, poster_url: url }))}
                  accept="image/*"
                  label="Upload poster (thumbnail)"
                />
                <Input testId="new-video-title" value={newVideo.title} onChange={(v) => setNewVideo((x) => ({ ...x, title: v }))} placeholder="Title" />
                <Input testId="new-video-tag" value={newVideo.tag} onChange={(v) => setNewVideo((x) => ({ ...x, tag: v }))} placeholder="Tag" />
                <button
                  data-testid="save-new-video"
                  onClick={addVideo}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white hover:opacity-90 disabled:opacity-60"
                  style={{ backgroundColor: '#0A3F22' }}
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  Add Video
                </button>
              </div>
            ) : (
              <button
                data-testid="new-video-btn"
                onClick={() => setNewVideo(emptyVideo())}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white hover:opacity-90"
                style={{ backgroundColor: '#D97706' }}
              >
                <Plus size={14} /> Add Video
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {videos.map((v) => (
              <div key={v.id} data-testid={`video-card-${v.id}`} className="bg-white rounded-xl overflow-hidden border relative group" style={{ borderColor: '#E5E7EB' }}>
                <div className="aspect-video bg-gray-100">
                  <video src={v.video_url} poster={v.poster_url} className="w-full h-full object-cover" preload="none" controls={false} />
                </div>
                <div className="p-3">
                  {v.tag && <span className="text-xs font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: '#EAF2EC', color: '#0A3F22' }}>{v.tag}</span>}
                  <p className="text-sm mt-1 font-semibold" style={{ color: '#0A3F22' }}>{v.title}</p>
                </div>
                <button
                  data-testid={`delete-video-${v.id}`}
                  onClick={() => delVideo(v.id)}
                  className="absolute top-2 right-2 p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ backgroundColor: '#DC2626', color: '#fff' }}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
